/**
 * Live subagent labels shared by the chat's spawn row, the composer's
 * background-work banner and the Agents panel, so a busy worker reads the
 * same everywhere: its latest step and how long it has run.
 *
 * Live labels tick once a second through DOM writes (zero React commits per
 * tick, as orchestration-v2's ProviderSubagentBar does), so a worker that
 * runs for fifteen minutes never re-renders the chat view. All labels share
 * one ticker on the second boundary, so a row and its banner never disagree
 * by a second. The write edits the text node React rendered instead of
 * replacing it, so a later render still updates the node on screen.
 */
import type { RuntimeSubagent } from "@amoreg/client-runtime/state/subagentRuntime";
import { isActiveSubagentStatus } from "@amoreg/client-runtime/state/subagentRuntime";
import {
  firstActivityLine,
  formatSubagentElapsedLabel,
  subagentActivityText,
} from "@amoreg/client-runtime/state/subagentStatus";
import { useEffect, useRef } from "react";

import { cn } from "~/lib/utils";
import { describeToolProgress } from "./toolPresentation";

type TickingAgent = Pick<RuntimeSubagent, "status" | "startedAt" | "completedAt">;

const tickListeners = new Set<(nowMs: number) => void>();
let tickTimer: ReturnType<typeof setTimeout> | null = null;

function scheduleTick() {
  // The next whole second, so every label reads the same clock value.
  tickTimer = setTimeout(
    () => {
      const nowMs = Date.now();
      for (const listener of tickListeners) listener(nowMs);
      scheduleTick();
    },
    1000 - (Date.now() % 1000),
  );
}

function onSecondTick(listener: (nowMs: number) => void): () => void {
  tickListeners.add(listener);
  if (tickTimer === null) scheduleTick();
  return () => {
    tickListeners.delete(listener);
    if (tickListeners.size === 0 && tickTimer !== null) {
      clearTimeout(tickTimer);
      tickTimer = null;
    }
  };
}

/** The agent's latest step (live) or outcome (settled), as one display line. */
export function describeSubagentActivity(
  agent: Pick<RuntimeSubagent, "status" | "progress" | "lastToolName" | "result" | "error">,
): string | null {
  const text = subagentActivityText(agent);
  return text === null ? null : firstActivityLine(describeToolProgress(text));
}

/** Text derived from an agent's timing, ticking while the agent is live. */
export function SubagentTickingText({
  agent,
  format,
  className,
}: {
  agent: TickingAgent;
  /** Must be stable (a module-level function), or the ticker restarts every render. */
  format: (agent: TickingAgent, nowMs: number) => string;
  className?: string | undefined;
}) {
  const ref = useRef<HTMLSpanElement>(null);
  const live = isActiveSubagentStatus(agent.status);
  const { status, startedAt, completedAt } = agent;

  useEffect(() => {
    if (!live) return;
    const update = (nowMs: number) => {
      const node = ref.current?.firstChild;
      const text = format({ status, startedAt, completedAt }, nowMs);
      if (node && node.nodeType === Node.TEXT_NODE) {
        node.nodeValue = text;
      } else if (ref.current) {
        ref.current.textContent = text;
      }
    };
    update(Date.now());
    return onSecondTick(update);
  }, [live, format, status, startedAt, completedAt]);

  return (
    <span ref={ref} className={cn("tabular-nums", className)}>
      {format(agent, Date.now())}
    </span>
  );
}

/** "4m 12s" while live, the run's own length once settled, nothing when untimed. */
export function SubagentElapsed({
  agent,
  className,
}: {
  agent: TickingAgent;
  className?: string | undefined;
}) {
  if (agent.startedAt === null) return null;
  return (
    <SubagentTickingText agent={agent} format={formatSubagentElapsedLabel} className={className} />
  );
}

/**
 * "4m 12s · Web search: Guernsey transfers": how long a worker has run and
 * what it is doing. The time leads so a narrow row truncates the step, never
 * the one part that shows the worker is alive. Either half is dropped when
 * unknown; nothing renders when both are.
 */
export function SubagentLiveDetail({
  agent,
  className,
}: {
  agent: RuntimeSubagent;
  className?: string | undefined;
}) {
  const activity = describeSubagentActivity(agent);
  const timed = agent.startedAt !== null;
  if (activity === null && !timed) return null;
  return (
    <span className={cn("min-w-0 truncate", className)}>
      {timed ? <SubagentElapsed agent={agent} /> : null}
      {activity !== null && timed ? " · " : null}
      {activity}
    </span>
  );
}
